import React, { FC } from "react";

import { BaseComponentProps, DataViewKey } from "@keepeek/commons";
import { ClickAwayListener, IconButton, Paper } from "@mui/material";
import clsx from "clsx";
import { animated, Spring } from "react-spring";

import { KIcon } from "../../../../components/common/KIcon";
import { KHeaderWidget } from "../../../../models/configuration/components/kHeaderWidget";
import { useKHeaderWidgetBurger } from "../hooks/useKHeaderWidgetBurger";
import KHeaderWidgetActionsBox from "../KHeaderWidgetActionsBox";
import { KHeaderWidgetComponentContext } from "../models";
import { classes, StyledKHeaderWidgetBurger } from "./styles";

type KHeaderWidgetBurgerProps = {
  widgetId: KHeaderWidget["id"];
  dataViewKey: DataViewKey;
  textColor: string;
} & BaseComponentProps;

const KHeaderWidgetBurger: FC<React.PropsWithChildren<KHeaderWidgetBurgerProps>> = function ({
  widgetId,
  className,
  testId = "KHeaderWidgetBurger",
  id,
  dataViewKey,
  textColor,
}) {
  const { isOpen, handleClose, handleOpen, components } = useKHeaderWidgetBurger({ widgetId });

  // Don't show the burger menu if we have no components in it
  if (components.length === 0) {
    return null;
  }
  return (
    <StyledKHeaderWidgetBurger
      data-testid={testId}
      id={id}
      className={clsx("KHeaderWidgetBurger", className, classes.root)}
      isOpen={isOpen}
    >
      <Spring from={{ opacity: 0, left: "-1rem" }} to={{ opacity: 1, left: "0" }}>
        {(style) => (
          <animated.div style={{ ...style, position: "relative" }}>
            <IconButton
              className={classes.burgerIconButton}
              sx={{ color: textColor }}
              onClick={handleOpen}
            >
              <KIcon className="fa-bars" fontSize="small" />
            </IconButton>
          </animated.div>
        )}
      </Spring>
      {isOpen && (
        <Spring from={{ opacity: 0 }} to={{ opacity: 1 }}>
          {(wallStyle) => (
            <animated.div className={classes.wall} style={wallStyle}>
              <ClickAwayListener onClickAway={handleClose}>
                <Spring from={{ left: "-100%" }} to={{ left: "0%" }}>
                  {(style) => (
                    <animated.div style={style} className={classes.sidebar}>
                      <Paper className={classes.sidebarPaper} elevation={12}>
                        <IconButton
                          onClick={handleClose}
                          sx={{ position: "absolute", top: 0, left: "100%" }}
                          className={clsx(classes.closeButton)}
                        >
                          <KIcon
                            className="fa-regular fa-circle-xmark"
                            fontSize="medium"
                            sx={{
                              color: (theme) => theme.palette.common.white,
                              overflow: "visible",
                            }}
                          />
                        </IconButton>
                        <KHeaderWidgetActionsBox
                          widgetId={widgetId}
                          context={KHeaderWidgetComponentContext.Burger}
                          dataViewKey={dataViewKey}
                        />
                      </Paper>
                    </animated.div>
                  )}
                </Spring>
              </ClickAwayListener>
            </animated.div>
          )}
        </Spring>
      )}
    </StyledKHeaderWidgetBurger>
  );
};

export default KHeaderWidgetBurger;
